// Scelta del comune: separa il comune gratuito (Osimo), quelli per abbonati
// OmniSurv e la demo per le pubbliche amministrazioni.

import React, { useEffect } from "react";
import Icon from "./Icon";
import { Button } from "./ui";
import SubscribeButton from "./SubscribeButton";
import { OMNI_LOGIN_URL } from "../lib/config";

const SECTIONS = [
  {
    kind: "free",
    title: "Gratuito",
    note: "Nessun account richiesto.",
    desc: "Zone del PRG sulla mappa e normativa applicata in PDF dall'area che disegni.",
    gpkgDesc: "Zone del PRG, ricerca per foglio e particella, esportazione DXF.",
    badge: { cls: "sm-badge--free", text: "Gratis" },
  },
  {
    kind: "pro",
    title: "Abbonati OmniSurv",
    note: "Si accede con il tuo account OmniSurv.",
    desc: "Zone del PRG, ricerca per foglio e particella, esportazione DXF.",
    badge: { cls: "sm-badge--pro", text: "Abbonati" },
  },
  {
    kind: "pa",
    title: "Comuni PA · demo",
    note: "Dimostrazione di SurvPA per le amministrazioni.",
    desc: "Normativa di zona, particelle, PDF e DXF.",
    badge: { cls: "sm-badge--pa", text: "Demo PA" },
  },
];

// Pitch per chi non è ancora abbonato: nasconderlo agli abbonati (OmniSurv o SurvMaps).
function ProPitch({ auth }) {
  if (auth.status === "paid") return null;
  const needsLogin = auth.status === "anon" || auth.status === "offline" || auth.status === "loading";
  return (
    <aside className="sm-picker-pro" aria-label="SurvMaps Pro">
      <span className="sm-badge sm-badge--pro">SurvMaps Pro</span>
      <h2 className="sm-picker-pro-title">Le norme del tuo intervento, non l'intero piano</h2>
      <p className="sm-picker-pro-text">
        Disegna l'area sulla mappa, descrivi in poche righe cosa vuoi fare: SurvMaps Pro incrocia zone del PRG,
        parametri urbanistici, prescrizioni delle NTA e norme regionali pertinenti, e te le consegna in un
        documento PDF pronto da allegare alla pratica — invece di spulciare l'intero piano a mano.
      </p>
      <ul className="sm-picker-pro-list">
        <li><Icon name="check" size={14} />Documento con le sole norme pertinenti all'intervento descritto</li>
        <li><Icon name="check" size={14} />Più comuni, ricerca per foglio e particella, esportazione DXF</li>
        <li><Icon name="check" size={14} />Generato dal piano vigente e dagli estratti ufficiali del Comune</li>
      </ul>
      {needsLogin ? (
        <a className="sm-btn sm-btn--primary sm-btn--block" href={OMNI_LOGIN_URL} target="_blank" rel="noopener noreferrer">
          Accedi o registrati
        </a>
      ) : (
        <SubscribeButton auth={auth} block />
      )}
      <p className="sm-picker-pro-text" style={{ fontSize: "var(--fs-2xs)" }}>
        Servizio SaaS: non costituisce prestazione professionale, i risultati vanno sempre verificati dal tecnico incaricato.
      </p>
    </aside>
  );
}

export default function CityPicker({ cities, auth, onSelect, onClose, kinds }) {
  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const locked = auth.status !== "paid";
  const showPitch = kinds.includes("pro");

  return (
    <div className="sm-picker">
      <div className="sm-picker-row">
        <div className="sm-picker-inner">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            <div style={{ flex: 1 }}>
              <h1 className="sm-picker-title">PRG SU MAPPA</h1>
              <p className="sm-picker-sub">
                Scegli il comune. Nei comuni gratuiti trovi le zone del piano senza account; con l'abbonamento
                OmniSurv si aggiungono altri comuni, con ricerca catastale ed esportazione DXF.
              </p>
            </div>
            {onClose && <Button variant="secondary" size="sm" icon="arrow-right" onClick={onClose}>Torna alla mappa</Button>}
          </div>

          {SECTIONS.filter((s) => kinds.includes(s.kind) && cities[s.kind]?.length).map((s) => (
            <section key={s.kind} className="sm-picker-section">
              <div className="sm-picker-section-head">
                <h2 className="sm-section-title">{s.title}</h2>
                <p>{s.note}</p>
              </div>
              <div className="sm-picker-grid">
                {cities[s.kind].map((c) => {
                  const isLocked = s.kind === "pro" && locked;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      className={`sm-card${isLocked ? " is-locked" : ""}`}
                      onClick={() => onSelect(c)}
                    >
                      {isLocked && <span className="sm-card-lock"><Icon name="lock" size={16} /></span>}
                      <span className="sm-card-name">{c.name}</span>
                      <span className="sm-card-desc">{(c.gpkg && s.gpkgDesc) || s.desc}</span>
                      <span className="sm-card-meta">
                        <span className={`sm-badge ${s.badge.cls}`}>{s.badge.text}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {showPitch && <ProPitch auth={auth} />}
      </div>
    </div>
  );
}
